import { useState } from "react";
import { ChevronUp, MessageSquareText, Plus, Minus } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

function FAQAccordion({ questionKey, categoryKey, isOpen, onToggle }) {
  const { t } = useTranslation();

  return (
    <div className="bg-white dark:bg-white/5 rounded-lg border-defaultBlack/10 border-[1px]">
      <button
        onClick={onToggle}
        className="w-full text-left px-4 py-3 flex items-center gap-4 justify-between focus:outline-none"
      >
        <span className="text-base font-semibold">
          {t(`migrationFaqs.${categoryKey}.questions.${questionKey}.question`)}
        </span>
        <div className="self-start">
          <ChevronUp
            className={`h-5 w-5 text-primary transition-all duration-300 ${
              isOpen ? "rotate-0" : "rotate-180"
            }`}
          />
        </div>
      </button>
      <div
        className={`px-6 overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? "max-h-96 pb-6" : "max-h-0"
        }`}
      >
        <p className="leading-relaxed text-sm text-gray-600 dark:text-gray-300 whitespace-pre-line">
          {t(`migrationFaqs.${categoryKey}.questions.${questionKey}.answer`)}
        </p>
      </div>
    </div>
  );
}

function FaqsSection() {
  const { t } = useTranslation();

  // Track open states by key name rather than index to avoid cross-category state bugs
  const [openCategory, setOpenCategory] = useState("general");
  const [openQuestion, setOpenQuestion] = useState(null);

  const toggleCategory = (categoryKey) => {
    setOpenCategory(openCategory === categoryKey ? null : categoryKey);
    setOpenQuestion(null); // Collapse nested question when switching categories
  };

  const toggleQuestion = (questionKey) => {
    setOpenQuestion(openQuestion === questionKey ? null : questionKey);
  };

  // Explicit array order to map through your i18n JSON structure securely
  const categories = [
    "general",
    "moneyAndSafety",
    "groupHistoryAndRecords",
    "forGroupTreasurers",
    "forGroupMembers",
    "afterMigration",
    "supportAndContact",
  ];

  return (
    <div
      className="pt-20 bg-white dark:bg-defaultBlack dark:text-white"
      id="faqs"
    >
      <div className="container mx-auto px-4 md:px-0">
        <h1 className="text-3xl font-bold mb-8">{t("faqsSection.title")}</h1>
        <div className="w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left Content - Nested Categories and FAQ List */}
            <div className="space-y-4">
              {categories.map((catKey) => {
                const isCatOpen = openCategory === catKey;

                // Securely fetch dynamic keys nested inside this translation category block
                const questionsObj =
                  t(`migrationFaqs.${catKey}.questions`, {
                    returnObjects: true,
                  }) || {};
                const questionKeys = Object.keys(questionsObj);

                return (
                  <div
                    key={catKey}
                    className="bg-gray-50 dark:bg-white/5 rounded-xl border border-defaultBlack/10 overflow-hidden"
                  >
                    {/* Outer Category Trigger */}
                    <button
                      onClick={() => toggleCategory(catKey)}
                      className="w-full text-left px-4 py-3 flex items-center justify-between gap-4 focus:outline-none bg-white dark:bg-white/5 hover:bg-gray-100/50 transition-colors"
                    >
                      <span className="text-lg font-bold text-primary">
                        {t(`migrationFaqs.${catKey}.title`)}
                      </span>
                      {/* Distinct Lucide Plus/Minus Category Icon */}
                      <div className="flex-shrink-0 transition-transform duration-200">
                        {isCatOpen ? (
                          <Minus className="h-4 w-4 " />
                        ) : (
                          <Plus className="h-4 w-4 " />
                        )}
                      </div>
                    </button>

                    {/* Inner Dynamic Nested Questions Shell */}
                    <div
                      className={`overflow-hidden ${
                        isCatOpen
                          ? "max-h-[2000px] p-6 space-y-3 border-t border-defaultBlack/10 bg-gray-50/50 dark:bg-black/10"
                          : "max-h-0"
                      }`}
                    >
                      {questionKeys.map((qKey) => (
                        <FAQAccordion
                          key={qKey}
                          categoryKey={catKey}
                          questionKey={qKey}
                          isOpen={openQuestion === qKey}
                          onToggle={() => toggleQuestion(qKey)}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Right Content - Contact Information */}
            <div className="flex items-center bg-white dark:bg-white/5 rounded-lg border-defaultBlack/10 border-[1px] shadow-sm sticky top-6">
              <div className="p-8 w-full text-center leading-loose">
                <div className="flex items-center justify-center bg-opacity-10 rounded-full mb-6">
                  <MessageSquareText className="w-16 h-16 stroke-[rgb(0,73,69)]" />
                </div>
                <h2 className="text-2xl font-bold mb-4">
                  {t("faqsSection.more.title")}
                </h2>
                <p>
                  {t("faqsSection.more.line1.part1")}{" "}
                  <Link
                    to="/help-center"
                    className="text-blue-500 font-bold hover:underline"
                  >
                    {t("faqsSection.more.line1.part2")}
                  </Link>{" "}
                  {t("faqsSection.more.line1.part3")}{" "}
                </p>
                <p>
                  <a
                    href="tel:+250794776745"
                    className="text-blue-500 font-bold hover:underline"
                  >
                    +250 794776745
                  </a>
                  , {t("faqsSection.more.line2")}{" "}
                </p>
                <p>
                  <a
                    href="mailto:info@uplus.rw"
                    className="text-blue-500 font-bold hover:underline"
                  >
                    info@uplus.rw
                  </a>{" "}
                  {t("faqsSection.more.line3")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FaqsSection;
